import React from 'react';
import { Editor } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextRedo2Icon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/redo2-icon';
import { RichTextUndo2Icon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/undo2-icon';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';

export type RichTextHistoryAction = 'undo' | 'redo';

/**
 * Props for the UndoRedoButton component.
 */
export interface RichTextUndoRedoButtonProps extends RichTextButtonProps {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * The history action to perform (undo or redo).
   */
  action: RichTextHistoryAction;
}

export const richTextHistoryIcons = {
  undo: RichTextUndo2Icon,
  redo: RichTextRedo2Icon,
};

export const richTextHistoryShortcutKeys: Partial<
  Record<RichTextHistoryAction, string>
> = {
  undo: 'Ctrl-z',
  redo: 'Ctrl-Shift-z',
};

export const richTextHistoryActionLabels: Record<
  RichTextHistoryAction,
  string
> = {
  undo: 'Undo',
  redo: 'Redo',
};

/**
 * Checks if a history action can be executed.
 *
 * @param editor The TipTap editor instance
 * @param action The history action to check
 * @returns Whether the action can be executed
 */
export function richTextCanExecuteHistoryAction(
  editor: Editor | null,
  action: RichTextHistoryAction,
): boolean {
  if (!editor) return false;
  return action === 'undo' ? editor.can().undo() : editor.can().redo();
}

/**
 * Executes a history action on the editor.
 *
 * @param editor The TipTap editor instance
 * @param action The history action to execute
 * @returns Whether the action was executed successfully
 */
export function richTextExecuteHistoryAction(
  editor: Editor | null,
  action: RichTextHistoryAction,
): boolean {
  if (!editor) return false;
  const chain = editor.chain().focus();
  return action === 'undo' ? chain.undo().run() : chain.redo().run();
}

/**
 * Determines if a history action should be disabled.
 *
 * @param editor The TipTap editor instance
 * @param action The history action to check
 * @param userDisabled Whether the action is explicitly disabled by the user
 * @returns Whether the action should be disabled
 */
export function richTextIsHistoryActionDisabled(
  editor: Editor | null,
  action: RichTextHistoryAction,
  userDisabled: boolean = false,
): boolean {
  if (userDisabled) return true;
  return !richTextCanExecuteHistoryAction(editor, action);
}

/**
 * Hook that provides all the necessary state and handlers for a history action.
 *
 * @param editor The TipTap editor instance
 * @param action The history action to handle
 * @param disabled Whether the action is explicitly disabled
 * @returns Object containing state and handlers for the history action
 */
export function useRichTextHistoryAction(
  editor: Editor | null,
  action: RichTextHistoryAction,
  disabled: boolean = false,
) {
  const canExecute = React.useMemo(
    () => richTextCanExecuteHistoryAction(editor, action),
    [editor, action],
  );

  const isDisabled = richTextIsHistoryActionDisabled(editor, action, disabled);

  const handleAction = React.useCallback(() => {
    if (!editor || isDisabled) return;
    richTextExecuteHistoryAction(editor, action);
  }, [editor, action, isDisabled]);

  const Icon = richTextHistoryIcons[action];
  const actionLabel = richTextHistoryActionLabels[action];
  const shortcutKey = richTextHistoryShortcutKeys[action];

  return {
    canExecute,
    isDisabled,
    handleAction,
    Icon,
    actionLabel,
    shortcutKey,
  };
}

/**
 * Button component for triggering undo/redo actions in a TipTap editor.
 */
export const RichTextUndoRedoButton = React.forwardRef<
  HTMLButtonElement,
  RichTextUndoRedoButtonProps
>(
  (
    {
      editor: providedEditor,
      action,
      text,
      className = '',
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const editor = useRichTextTiptapEditor(providedEditor);

    const { isDisabled, handleAction, Icon, actionLabel, shortcutKey } =
      useRichTextHistoryAction(editor, action, disabled);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !disabled) {
          handleAction();
        }
      },
      [onClick, disabled, handleAction],
    );

    if (!editor || !editor.isEditable) {
      return null;
    }

    return (
      <RichTextButton
        ref={ref}
        type="button"
        className={className.trim()}
        disabled={isDisabled}
        data-style="ghost"
        data-disabled={isDisabled}
        role="button"
        tabIndex={-1}
        aria-label={actionLabel}
        tooltip={actionLabel}
        shortcutKeys={shortcutKey}
        onClick={handleClick}
        {...buttonProps}
      >
        {children || (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </RichTextButton>
    );
  },
);

RichTextUndoRedoButton.displayName = 'UndoRedoButton';

export default RichTextUndoRedoButton;
